import { parseMentionCsv } from "./utils";

export interface MentionUploadOutcome {
  error: string | null;
  validIds: number[];
  /** Cells that are not valid ids, plus well-formed ids the taxonomy does not know. */
  invalidValues: string[];
}

const failure = (error: string): MentionUploadOutcome => ({
  error,
  validIds: [],
  invalidValues: [],
});

/**
 * Parses the uploaded CSV and checks its ids against the taxonomy.
 * `fetchKnownIds` resolves to the subset of the given ids that exist.
 */
export const validateMentionFile = async (
  file: File,
  fetchKnownIds: (ids: number[]) => Promise<Set<number>>
): Promise<MentionUploadOutcome> => {
  try {
    const parsed = await parseMentionCsv(file);
    if (parsed.error) return failure(parsed.error);

    const knownIds =
      parsed.ids.length > 0
        ? await fetchKnownIds(parsed.ids)
        : new Set<number>();
    const validIds = parsed.ids.filter((id) => knownIds.has(id));
    if (validIds.length === 0) {
      const examples = [...parsed.invalidValues, ...parsed.ids.map(String)]
        .slice(0, 3)
        .join(", ");
      return failure(
        `No valid mentions found. All mention IDs were invalid.${examples ? ` For example: ${examples}.` : ""}`
      );
    }

    return {
      error: null,
      validIds,
      invalidValues: [
        ...parsed.invalidValues,
        ...parsed.ids.filter((id) => !knownIds.has(id)).map(String),
      ],
    };
  } catch (error) {
    return failure(
      error instanceof Error ? error.message : "An unknown error occurred"
    );
  }
};
